import { useEffect, useRef, useState } from 'react'
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk'
import { useApp, useAuth, useTheme } from 'hooks'
import { toast } from 'react-toastify'
import getDashboardList from '../../../Admin/Configuration/use-cases/get-dashboard-list-use-case'
import getQuickSightTopicURL from '../use-cases/getQuickSightTopicURL'
import './topic.scss'

function QuickSightTopic() {
  const { user } = useAuth()
  const { repoFactory } = useApp()
  const { theme } = useTheme()
  const containerRef = useRef(null)

  const [isContentLoaded, setIsContentLoaded] = useState(false)
  const [isEmbedded, setIsEmbedded] = useState(false)
  const [accountId, setAccountId] = useState(null)
  const [accountRegion, setAccountRegion] = useState(null)
  const [themeIds, setThemeIds] = useState({})
  const [topicList, setTopicList] = useState([])
  const [dashboardUrl, setDashboardUrl] = useState(null)

  useEffect(() => {
    getDashboardList(
      { token: user?.authToken },
      {
        biRepo: repoFactory.biRepo(),
        observer: {
          success: (response) => {
            const { accountId, accountRegion, topicList, enableCustomThemes, customThemes, defaultThemes } = response

            let themeIds = null
            if (enableCustomThemes && customThemes?.length > 0) {
              themeIds = getThemeIds(customThemes)
            } else if (defaultThemes?.length > 0) {
              themeIds = getThemeIds(defaultThemes)
            }

            setAccountId(accountId)
            setAccountRegion(accountRegion)
            setThemeIds(themeIds)

            const topics = topicList.map((topic) => ({
              value: topic.value,
              label: topic.name
            }))
            setTopicList(topics)
          },
          failure: (e) => {
            console.error(e.message)
            toast.error('Error while getting dashboard list')
          }
        }
      }
    )
  }, [repoFactory, user?.authToken])

  useEffect(() => {
    if (topicList.length > 0 && themeIds) {
      const topicIdList = topicList.map((item) => item.value)
      const themeIdList = [themeIds.darkThemeId, themeIds.lightThemeId].filter(Boolean)

      getQuickSightTopicURL(
        {
          username: user?.username,
          topicIdList,
          themeList: themeIdList
        },
        {
          biRepo: repoFactory.biRepo(),
          observer: {
            errorGettingUrl: () => {
              toast.error('Error while getting embed URL')
            },
            receiveQuickSightUrl: (response) => {
              setDashboardUrl(response.EmbedUrl)
            }
          }
        }
      )
    }
  }, [repoFactory, topicList, themeIds, user?.username, theme]) // theme triggers fresh embed URL

  // 🧼 Reset and clear previous iframe whenever a new embed URL is set
  useEffect(() => {
    if (dashboardUrl) {
      setIsEmbedded(false)
      setIsContentLoaded(false)
      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }
    }
  }, [dashboardUrl])

  // 🚀 Embed Q Search only when everything is ready
  useEffect(() => {
    const embedQSearch = async () => {
      if (!dashboardUrl || !containerRef.current || isEmbedded) return

      try {
        const embeddingContext = await createEmbeddingContext()

        const frameOptions = {
          url: dashboardUrl,
          container: containerRef.current,
          width: '100%',
          height: '38px',
          resizeHeightOnSizeChangedEvent: true
        }

        const qSearchOptions = {
          panelOptions: {
            panelType: 'SEARCH_BAR',
            focusedHeight: '100px',
            expandedHeight: '700px'
          },
          hideTopicName: false,
          hideIcon: true,
          showTopicName: true,
          showPinboard: true,
          allowTopicSelection: true,
          allowFullscreen: true,
          searchPlaceholderText: 'Ask about your spending?',
          qactions_remote: `${dashboardUrl}/qactions`,
          onMessage: (messageEvent) => {
            if (messageEvent.eventName === 'CONTENT_LOADED') {
              setIsContentLoaded(true)
            }
          }
        }

        if (themeIds && accountId && accountRegion) {
          qSearchOptions.themeOptions = {
            themeArn: `arn:aws:quicksight:${accountRegion}:${accountId}:theme/${
              theme === 'dark' ? themeIds.darkThemeId : themeIds.lightThemeId
            }`
          }
        }

        await embeddingContext.embedGenerativeQnA(frameOptions, qSearchOptions)
        setIsEmbedded(true)
      } catch (error) {
        console.error('Embedding failed:', error)
        toast.error('Embedding Q Search failed')
      }
    }

    embedQSearch()
  }, [dashboardUrl, isEmbedded, containerRef, accountId, accountRegion, theme, themeIds])

  const getThemeIds = (themes = []) => ({
    darkThemeId: themes.find((theme) => theme.type === 'dark')?.id || '',
    lightThemeId: themes.find((theme) => theme.type === 'light')?.id || ''
  })

  return (
    <div>
      {!isContentLoaded && <div className="text-center">Loading...</div>}
      <div className={`q-search-container ${isContentLoaded ? 'visible' : 'invisible'}`} ref={containerRef} />
    </div>
  )
}

export default QuickSightTopic
